import { useEffect, useState } from "react";
import { api, ApiError, currentOrgId, setEditHooks, type EditOp } from "../api/client";
import { recordExtras } from "../api/customize";
import { idb } from "./idb";
import { isOnline, onNetwork } from "./network";
import { conflictOf, exportShape, newItem, replay, retryItem, type OutboxItem, type OutboxStore, type Receipt, type SendOptions, type Sent } from "./outbox-core";
import { baseOf, DISCARDED_NOTE, numbersHeld, resolveEdit, type Choice } from "./edit-core";
import { keptLookup, patchKept } from "./offlineCache";
import { nextOfflineNumber, refreshBlocks, deviceId, spendNumber } from "./numberBlocks";
import { t } from "../i18n";
import { todayIst } from "../ui/format";
import { checkOffline, fieldModuleOf, type Field, type Lookups } from "./validate-core";


/*
 * What is recorded with no connection waits here (IndexedDB "outbox", this device only) and is sent when the server
 * answers again, each with the Idempotency-Key it was given when it was made: expenses, invoice payments, draft invoices
 * and estimates, notes on customers and vendors, timesheet entries. The replay rules are in outbox-core.ts. Every send goes through sendOrQueue(), online too, so a double tap or a lost answer never records twice.
 * The items belong to the staff member who made them; sign-out deletes them (after asking, while any are unsent).
 * Each item is sent to the organization it was made in (X-Org-Id), not the one the screen has switched to since; an item
 * for an organization the person no longer belongs to (setMemberOrgs, from the sign-in reply and every online check in
 * orgCheck.ts) is not sent at all: the outbox says so and offers Export (a JSON file, with the receipt photo) and Discard.
 * Wave 8 (docs/implemented-offline8.md): an edit made online carries its version too, and a 409 opens the same conflict view
 * as an offline one ("crm:edit-conflict", PwaUi.tsx); discarding an issue that never reached the server marks its number
 * used on the device, refused (a "refuse" item), because the invoice was printed with it.
 * Wave 9 (docs/implemented-offline9.md): every other edit too (leads, bills, settings…: src/api/client.ts setEditHooks) waits
 * here with its version when there is no connection, and meets the same conflict view when someone saved meanwhile; an issue
 * refused for a reason the device can fix is sent again with the same number (resendFixed); new customers, vendors and items
 * made with no connection wait like expenses.
 */

export type { OutboxItem };

const store: OutboxStore = {
  list: () => idb.all<OutboxItem>("outbox"),
  put: (i) => idb.put("outbox", i),
  remove: (id) => idb.del("outbox", id),
};

const orgHeader = (org: number) => ({ "X-Org-Id": String(org) });
const versionHeader = (v: string | null | undefined): Record<string, string> => (v ? { "X-Base-Version": v } : {});
const sender = {
  // An edit is a PATCH; an edit, issue or approval made offline names the version the person saw (server/offline-sync.js).
  send: async (path: string, body: unknown, key: string, org: number, opts?: SendOptions) => {
    const out = await api<Sent>(opts?.method ?? "POST", path, body, { "Idempotency-Key": key, ...orgHeader(org), ...versionHeader(opts?.version) });
    // The kept copy catches up with what was just written, so the next offline edit starts from it (a GET keeps it).
    if (opts?.method === "PATCH" && org === (currentOrgId() ?? 1)) void api("GET", path).catch(() => undefined);
    return out;
  },
  upload: (recordId: string, r: Receipt, key: string, org: number) => recordExtras.upload("expenses", recordId, r.blob, r.name, { "Idempotency-Key": key, ...orgHeader(org) }).then(() => undefined),
};

let owner: string | null = null;
let orgName: string | null = null;
/** The organizations the person belongs to now; null until known (then nothing is held back). */
let member: Set<number> | null = null;
let running: Promise<void> | null = null;
const listeners = new Set<() => void>();
const changed = () => listeners.forEach((l) => l());
const newKey = () => `ob_${crypto.randomUUID()}`;

/**
 * Signed in (with a token): this person's items may be sent, each to its own organization; anyone else's are deleted.
 * Null: nothing is sent. `orgName` labels what is queued from now on.
 */
export async function setOutboxOwner(id: string | null, name: string | null = null) {
  owner = id;
  orgName = name;
  if (!id) member = null;
  if (!id) return;
  const all = await store.list().catch(() => [] as OutboxItem[]);
  await Promise.all(all.filter((i) => i.owner !== id).map((i) => store.remove(i.id)));
  changed();
  void flush();
}

/** Wave 9: what an edit of any record is called in the outbox: its name, number or title, else what it is. */
function editLabel(op: EditOp) {
  const b = (op.base ?? {}) as Record<string, unknown>;
  const name = [b.display_name, b.full_name, b.name, b.number, b.label, b.title].find((v) => typeof v === "string" && v.trim());
  return name ? t("Edit · {name}", { name: String(name) }) : t("Edit · {what}", { what: op.path.split("?")[0].split("/").slice(2).join(" › ") });
}
const editItem = (op: EditOp, key: string) => newItem({ owner: owner ?? "", org: op.org, org_name: orgName, kind: "edit", label: editLabel(op), path: op.path, body: op.body, method: op.method, version: op.version, record: op.path, base: baseOf(op.base, op.body as Record<string, unknown>) }, key);
const editable = (op: EditOp) => Boolean(owner) && op.body !== null && typeof op.body === "object" && !Array.isArray(op.body);

// Wave 9: an edit made anywhere in the CRM (not through editOrQueue) that cannot reach the server, or that meets someone
// else's change, comes here with the version its screen saw (src/api/client.ts).
setEditHooks({
  queue: async (op) => {
    if (!editable(op)) return null;
    await checkKept(op.method, op.path, op.body);
    await store.put(editItem(op, newKey()));
    await patchKept(op.path, op.body as Record<string, unknown>).catch(() => undefined);
    changed();
    return t("No connection: the change is kept on this device and sent when you're back online. If someone changes it meanwhile, you'll choose what to keep.");
  },
  conflict: async (op) => {
    const c = conflictOf({ status: 409, message: "", data: op.data });
    if (!editable(op) || !c) return null;
    await store.put({ ...editItem(op, newKey()), state: "conflict", error: (op.data as { detail?: string } | undefined)?.detail ?? null, conflict: c, attempts: 1 });
    changed();
    window.dispatchEvent(new CustomEvent("crm:edit-conflict", { detail: { record: op.path } }));
    return t("Someone else changed this record while you had it open. Nothing was overwritten: choose what to keep in the window that opened.");
  },
});

/**
 * Wave 9: what the server would refuse, checked with the copies kept on this device before anything is kept for sending
 * (validate-core.ts). Throws the reason (422), so the form shows it at once instead of a refusal hours later.
 */
async function checkKept(method: string, path: string, body: unknown) {
  const look: Lookups = { today: todayIst() };
  const items = <T,>(v: unknown) => ((v as { items?: T[] } | null)?.items ?? null);
  const draft = path === "/admin/offline/issue" && body && typeof body === "object" && "draft" in body;
  if (path.startsWith("/admin/sales/") || path.startsWith("/admin/offline/")) {
    look.customers = items(await keptLookup("/admin/customers?status=active").catch(() => null));
    look.items = items(await keptLookup("/admin/items?status=active").catch(() => null));
  }
  if (path.startsWith("/admin/purchases/expenses")) look.accounts = (await keptLookup<{ accounts?: Lookups["accounts"] }>("/admin/purchases/meta").catch(() => null))?.accounts ?? null;
  if (path.startsWith("/admin/projects/time-entries")) look.projects = (await keptLookup<{ my_projects?: { id: string }[] }>("/admin/projects/meta").catch(() => null))?.my_projects ?? null;
  const pay = /^\/admin\/sales\/invoices\/([^/]+)\/payments$/.exec(path);
  if (pay) look.invoice = await keptLookup<{ balance?: string; issue_date?: string }>(`/admin/sales/invoices/${pay[1]}`).catch(() => null);
  const module = draft ? "invoices" : fieldModuleOf(method, path);
  if (module) look.fields = (await keptLookup<{ fields?: Field[] }>(`/admin/ext/custom-values?module=${module}`).catch(() => null))?.fields ?? null;
  const wrong = checkOffline(method, path, body, look, t);
  if (wrong) throw new ApiError(422, wrong);
}

/** Sends what is waiting, once at a time. */
export function flush(): Promise<void> {
  if (!owner || !isOnline()) return Promise.resolve();
  running ??= replay(store, sender, owner, (org) => !member || member.has(org))
    .then(
      (r) => {
        if (r.sent || r.conflicts) window.dispatchEvent(new CustomEvent("crm:outbox-sent", { detail: r }));
        // Back online with nothing waiting: the device's reserved invoice numbers are brought up to date (numberBlocks.ts),
        // keeping those the outbox still holds (wave 8).
        if (!r.waiting) void heldNumbers().then(refreshBlocks);
      },
      () => undefined,
    )
    .finally(() => {
      running = null;
      changed();
    });
  return running;
}

let started = false;
function start() {
  if (started) return;
  started = true;
  onNetwork(() => {
    if (isOnline()) void flush();
  });
}

export type Queued<T> = { queued: false; result: T; receiptError: string | null } | { queued: true };

/**
 * Records now, or keeps it for later when the server cannot be reached. A refusal (a wrong amount, no permission) is
 * thrown as usual, so the form shows it; only "no connection" queues.
 */
export async function sendOrQueue<T extends { id?: string }>(op: { kind: OutboxItem["kind"]; label: string; path: string; body: unknown; receipt?: Receipt | null; version?: string | null; record?: string | null }): Promise<Queued<T>> {
  const key = newKey();
  // Fixed now: a switch to another organization before the replay must not move this into that organization's books.
  const org = currentOrgId() ?? 1;
  const queue = async (extra: Partial<OutboxItem> = {}) => {
    if (!owner) throw new ApiError(0, t("The CRM server could not be reached, and offline saving is off in this browser."));
    if (!extra.stage) await checkKept("POST", op.path, op.body); // wave 9
    await store.put({ ...newItem({ owner, org, org_name: orgName, ...op }, key), ...extra });
    changed();
    return { queued: true } as const;
  };
  let result: T;
  try {
    result = await api<T>("POST", op.path, op.body, { "Idempotency-Key": key, ...orgHeader(org), ...versionHeader(op.version) });
  } catch (e) {
    // Not reached, or no answer: it may have arrived. The same key later makes sure it counts once either way.
    if (e instanceof ApiError && e.status === 0) return queue();
    throw e;
  }
  if (!op.receipt) return { queued: false, result, receiptError: null };
  try {
    await sender.upload(result.id ?? "", op.receipt, `${key}:receipt`, org);
    return { queued: false, result, receiptError: null };
  } catch (e) {
    if (e instanceof ApiError && e.status === 0) {
      await queue({ stage: "receipt", record_id: result.id ?? null });
      return { queued: false, result, receiptError: t("No connection: the photo will be attached when you are back online.") };
    }
    return { queued: false, result, receiptError: e instanceof Error ? e.message : String(e) };
  }
}

/* ---------- wave 7: edits, invoices issued and approvals decided with no connection (docs/implemented-offline7.md) ---------- */

/** An earlier item for the same record still waiting or in conflict: a new one goes behind it, never round it. */
async function behind(org: number, record: string) {
  return (await store.list().catch(() => [] as OutboxItem[])).some((i) => i.owner === owner && (i.org ?? 1) === org && i.record === record && i.state !== "flagged");
}

/**
 * Saves an edit of a customer, vendor, item, draft invoice, estimate, expense or time entry, or keeps it on this device when
 * there is no connection. `record` is the record as the form opened it: its updated_at is the version the edit builds on,
 * and its values are what the person started from (for a conflict). Online the edit is sent as before; queued, it is sent
 * later with that version, and the server refuses it with both copies if the record changed meanwhile.
 */
export async function editOrQueue<T>(op: { label: string; path: string; body: Record<string, unknown>; record: object }): Promise<Queued<T>> {
  const key = newKey();
  const org = currentOrgId() ?? 1;
  const version = (op.record as { updated_at?: unknown }).updated_at;
  const queue = async () => {
    if (!owner) throw new ApiError(0, t("The CRM server could not be reached, and offline saving is off in this browser."));
    if (typeof version !== "string" || !version) throw new ApiError(0, t("No connection, and this record can't be changed offline: open it once while online first."));
    await checkKept("PATCH", op.path, op.body); // wave 9
    await store.put(newItem({ owner, org, org_name: orgName, kind: "edit", label: op.label, path: op.path, body: op.body, method: "PATCH", version, record: op.path, base: baseOf(op.record, op.body) }, key));
    await patchKept(op.path, op.body).catch(() => undefined);
    changed();
    void flush();
    return { queued: true } as const;
  };
  if (!isOnline() || (await behind(org, op.path))) return queue();
  try {
    // Wave 8: online too, the edit names the version it was opened at, so a change someone made meanwhile is never overwritten.
    const result = await api<T>("PATCH", op.path, op.body, { "Idempotency-Key": key, ...orgHeader(org), ...versionHeader(typeof version === "string" ? version : null) });
    return { queued: false, result, receiptError: null };
  } catch (e) {
    // Not reached, or the answer was lost: the same key makes sure it counts once.
    if (e instanceof ApiError && e.status === 0) return queue();
    const c = e instanceof ApiError ? conflictOf(e) : null;
    if (c && owner && typeof version === "string") {
      // Both copies go to the outbox as a conflict, and the conflict view opens over the form (PwaUi.tsx).
      const item: OutboxItem = { ...newItem({ owner, org, org_name: orgName, kind: "edit", label: op.label, path: op.path, body: op.body, method: "PATCH", version, record: op.path, base: baseOf(op.record, op.body) }, newKey()), state: "conflict", error: (e as Error).message, conflict: c, attempts: 1 };
      const kept = await store.put(item).then(() => true, () => false);
      if (kept) {
        changed();
        window.dispatchEvent(new CustomEvent("crm:edit-conflict", { detail: { record: op.path } }));
        throw new ApiError(409, t("Someone else changed this record while you had it open. Nothing was overwritten: choose what to keep in the window that opened."), (e as ApiError).data);
      }
    }
    throw e;
  }
}

/**
 * Issues an invoice with no connection, with the next number reserved to this device (numberBlocks.ts): an existing draft
 * (`invoice`, as the screen shows it) or a new one (`draft`, the body of a new invoice). `shown` is the total and GST the
 * person saw. Refused, with the reason, when the role may not issue or the device has no number left. Returns the number.
 */
export async function queueIssue(op: { label: string; day: string; invoice?: { id: string; updated_at: string }; draft?: Record<string, unknown>; shown: { total: string; tax?: string | null } }): Promise<string> {
  if (!owner) throw new ApiError(0, t("The CRM server could not be reached, and offline saving is off in this browser."));
  const org = currentOrgId() ?? 1;
  if (op.draft) await checkKept("POST", "/admin/offline/issue", { draft: op.draft }); // wave 9: before a number is printed
  const all = await store.list().catch(() => [] as OutboxItem[]);
  const next = await nextOfflineNumber(all.filter((i) => i.owner === owner), org, op.day);
  if ("refused" in next) throw new ApiError(0, next.refused);
  await spendNumber(next.number); // wave 8: on an invoice now, whatever happens to it
  const record = op.invoice ? `/admin/sales/invoices/${op.invoice.id}` : null;
  // Wave 9: issued_at is this device's clock; the server corrects it by the skew it recorded when the block was reserved.
  const body = { device_id: await deviceId(), number: next.number, ...(op.invoice ? { invoice_id: op.invoice.id } : { draft: op.draft }), shown: { total: op.shown.total, ...(op.shown.tax ? { tax: op.shown.tax } : {}) }, issued_at: new Date().toISOString() };
  await store.put(newItem({ owner, org, org_name: orgName, kind: "issue", label: `${next.number} · ${op.label}`, path: "/admin/offline/issue", body, version: op.invoice?.updated_at ?? null, record }, newKey()));
  changed();
  void flush();
  return next.number;
}

/**
 * A conflicted edit settled by the person: "mine" sends what they typed over the server's copy as it is now, "theirs" keeps
 * the server's (the item goes), a map merges field by field. The new item has a new key (its body differs).
 */
export async function resolveConflict(id: string, choice: Choice | Record<string, Choice>) {
  const i = await idb.get<OutboxItem>("outbox", id);
  if (!i) return;
  const next = resolveEdit(i, choice, newKey());
  await store.remove(id);
  if (next) {
    await store.put(next);
    await patchKept(next.path, next.body as Record<string, unknown>).catch(() => undefined);
  }
  changed();
  await flush();
}

/**
 * An issue or approval refused because the record changed: sent again against what the server has now (a new key), once the
 * person has looked at it. The server checks everything else again (a request decided meanwhile is still refused).
 */
export async function sendAgainOnCurrent(id: string) {
  const i = await idb.get<OutboxItem>("outbox", id);
  if (!i?.conflict) return;
  await store.remove(id);
  // Wave 8: a payment on an onboarding instalment is checked by the amount due (its body), not by a version header.
  const pay = i.kind === "payment" && i.body && typeof i.body === "object" && "expected_amount" in i.body;
  await store.put({ ...retryItem(i), id: newKey(), ...(pay ? { body: { ...(i.body as object), expected_amount: i.conflict.version } } : { version: i.conflict.version }) });
  changed();
  await flush();
}

/**
 * Wave 9: an issue the server refused for a reason the device can fix, sent again with the SAME number (it stays reserved to
 * this device until the block's end). A new invoice goes with its corrected body (`draft`); an existing draft as it is now
 * on this device (fixed with its own edit, which goes first), with its version and total from the kept copy. A new key: the
 * first answer is kept under the old one.
 */
export async function resendFixed(id: string, draft?: Record<string, unknown> | null, shown?: { total: string }, savedAt?: string) {
  const i = await idb.get<OutboxItem>("outbox", id);
  if (!i?.fixable || i.kind !== "issue") return;
  const body = { ...(i.body as Record<string, unknown>) };
  let version = i.version ?? null;
  if (draft) body.draft = draft;
  else if (i.record) {
    const kept = await keptLookup<{ updated_at?: string; total?: string }>(i.record).catch(() => null);
    if (kept?.updated_at) version = kept.updated_at;
    if (kept?.total) body.shown = { total: kept.total };
  }
  if (shown) body.shown = shown;
  if (savedAt) version = savedAt; // the draft as its fix just saved it
  await store.remove(id);
  // Made now, so an edit of the draft queued meanwhile (the fix) is sent before it.
  await store.put({ ...retryItem(i), id: newKey(), body, version, created_at: new Date().toISOString() });
  changed();
  await flush();
}

/** Wave 8: the numbers this person's outbox holds in this organization (issues unsent or refused, numbers still to mark). */
export async function heldNumbers(): Promise<string[]> {
  const org = currentOrgId() ?? 1;
  const mine = (await store.list().catch(() => [] as OutboxItem[])).filter((i) => i.owner === owner);
  return [...numbersHeld(mine, org)];
}

/** The organizations the person belongs to now (sign-in, and each online check): items for any other wait to be exported or discarded. */
export function setMemberOrgs(ids: number[]) {
  member = new Set(ids);
  changed();
  void flush();
}

/** Made in an organization the person has since left: it will not be sent. */
export const inLeftOrg = (i: OutboxItem) => Boolean(member && !member.has(i.org ?? 1));

const dataUrl = (b: Blob) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error ?? new Error("The photo could not be read."));
    r.readAsDataURL(b);
  });

/** Saves an item as a JSON file on the device (with its receipt photo), so nothing typed is lost when it cannot be sent. */
export async function exportItem(i: OutboxItem) {
  const photo = i.receipt ? await dataUrl(i.receipt.blob).catch(() => null) : null;
  const blob = new Blob([JSON.stringify(exportShape(i, photo), null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `outbox-${i.kind}-${i.created_at.slice(0, 10)}-${i.id.slice(3, 11)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function retry(id: string) {
  const i = await idb.get<OutboxItem>("outbox", id);
  if (i) await store.put(retryItem(i));
  changed();
  await flush();
}

export async function discard(id: string) {
  const i = await idb.get<OutboxItem>("outbox", id).catch(() => undefined);
  await store.remove(id);
  // Wave 8: an issue that never reached the server was still printed with its number on this device. The number is marked
  // used on the device, refused (sent like any item), so it never goes on another invoice. A refused one is marked already.
  const number = i?.kind === "issue" && i.state !== "flagged" && !i.refused ? (i.body as { number?: unknown }).number : null;
  if (i && owner && typeof number === "string") {
    await store.put(newItem({ owner, org: i.org, org_name: i.org_name, kind: "refuse", label: t("{number}: discarded on this device, never to be used", { number }), path: "/admin/offline/numbers/refuse", body: { device_id: (i.body as { device_id?: string }).device_id ?? (await deviceId()), numbers: [number], note: DISCARDED_NOTE } }, newKey()));
    void flush();
  }
  changed();
}

/** Sign-out: everything waiting is deleted with the rest of the offline data. */
export async function clearOutbox() {
  owner = null;
  await idb.clear("outbox").catch(() => undefined);
  changed();
}

export async function pendingCount() {
  // A flagged invoice was sent; it only waits to be read.
  return (await store.list().catch(() => [] as OutboxItem[])).filter((i) => i.state !== "flagged").length;
}

/** This person's items, oldest first, kept up to date. */
export function useOutbox() {
  const [items, setItems] = useState<OutboxItem[]>([]);
  useEffect(() => {
    start();
    let live = true;
    const read = () =>
      void store.list().then(
        (all) => live && setItems(all.filter((i) => !owner || i.owner === owner).sort((a, b) => a.created_at.localeCompare(b.created_at))),
        () => undefined,
      );
    read();
    listeners.add(read);
    return () => {
      live = false;
      listeners.delete(read);
    };
  }, []);
  return items;
}

/** A camera photo can be 5 to 12 MB; uploads stop at 5 MB. A photo is made at most 2000 px on its long side, as JPEG. */
export async function shrinkPhoto(file: File): Promise<Receipt> {
  const keep = { name: file.name || "receipt.jpg", type: file.type, blob: file as Blob };
  if (!/^image\/(jpeg|png|webp)$/.test(file.type) || typeof createImageBitmap !== "function") return keep;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2000 / Math.max(bmp.width, bmp.height));
    if (scale === 1 && file.size < 1_500_000) return keep;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext("2d")?.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, "image/jpeg", 0.82));
    if (!blob) return keep;
    return { name: `${(file.name || "receipt").replace(/\.[^.]+$/, "")}.jpg`, type: "image/jpeg", blob };
  } catch {
    return keep;
  }
}
